import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuote } from "./useQuote";
import { useOffer } from "./useOffer";
import { pushGTMEvent } from "utils/gtmEvent";
import { GA_EVENT_CONFIGS } from "constants/gaEvents";
import type { DTOApplicationFormReturn } from "types/Card";
import { mapCoverageOptionsWithPremiums } from "../utils/quoteUtils";
import { ROUTES } from "routes/constants";
import { useProductContext } from "context/ProductContext";

interface UseDTOQuoteFormProps {
    form: DTOApplicationFormReturn;
    quoteFormData: {
        dateOfBirth?: { month?: string; day?: string; year?: string };
        gender?: string;
        state?: string;
        nicotineUsage?: string;
    };
}

/**
 * Checks if all required form fields are filled
 */
const areAllFieldsFilled = (
    dobMonth?: string,
    dobDay?: string,
    dobYear?: string,
    gender?: string,
    stateValue?: string,
    nicotineUsage?: string
): boolean => {
    return !!(dobMonth && dobDay && dobYear && gender && stateValue && nicotineUsage !== undefined && nicotineUsage !== "");
};

/**
 * Validates all quote form fields
 */
const validateQuoteFields = async (trigger: any): Promise<boolean> => {
    const results = await Promise.all([
        trigger("quote.dateOfBirth.month"),
        trigger("quote.dateOfBirth.day"),
        trigger("quote.dateOfBirth.year"),
        trigger("quote.gender"),
        trigger("quote.state"),
        trigger("quote.nicotineUsage"),
    ]);
    return results.every((result) => result);
};

/**
 * Determines the default coverage index based on number of options
 */
const getDefaultCoverageIndex = (optionsLength: number): number => {
    switch (optionsLength) {
        case 2:
        case 3:
            // If 2 offers - Show the highest coverage (last option)
            // If 3 offers - Show the middle coverage
            return 1;
        case 4:
            // If 4 offers - Show the 3rd highest coverage
            return 2;
        default:
            // For any other number, default to first option
            return 0;
    }
};

/**
 * Restores date of birth fields from saved quote data
 */
const restoreDateOfBirth = (quoteData: any, setValue: any): void => {
    if (quoteData.dobMonth) setValue("quote.dateOfBirth.month", quoteData.dobMonth);
    if (quoteData.dobDay) setValue("quote.dateOfBirth.day", quoteData.dobDay);
    if (quoteData.dobYear) setValue("quote.dateOfBirth.year", quoteData.dobYear);
};

/**
 * Restores basic form fields from saved quote data
 */
const restoreBasicFields = (quoteData: any, setValue: any): void => {
    if (quoteData.gender) setValue("quote.gender", quoteData.gender);
    if (quoteData.state) setValue("quote.state", quoteData.state);
    if (quoteData.hasUsedNicotineLastYear !== undefined) {
        setValue("quote.nicotineUsage", quoteData.hasUsedNicotineLastYear ? "true" : "false");
    }
};

/**
 * Custom hook to manage DTO (Direct Term Online) quote form business logic
 * Handles state management, effects, validations, and API calls for term life insurance quotes
 */
export const useDTOQuoteForm = ({ form, quoteFormData }: UseDTOQuoteFormProps) => {
    const navigate = useNavigate();
    const {
        loadSavedQuoteData,
        quoteData,
        fetchCoverageOptions,
        isLoadingQuotes,
        saveCoverageSelection,
        saveQuote,
        clearQuote,
        storeQuote,
    } = useQuote();
    const { offerData, resetApplicationKeepOffer } = useOffer();
    const { isDTO } = useProductContext();
    const { trigger, setValue } = form;

    // ========== State ==========
    const [activeTab, setActiveTab] = useState<"myself" | "spouse">("myself");
    const [coverageOptions, setCoverageOptionsState] = useState<Array<{ coverageAmount: number; monthlyPremium: number }>>([]);
    const [selectedCoverage, setSelectedCoverage] = useState(0);
    const [isQuoteFormValid, setIsQuoteFormValid] = useState(false);
    const isInitialized = useRef(false);

    // ========== Computed Values ==========
    const offerCoverageAmounts = useMemo(
        () =>
            offerData?.offer
                ? offerData.offer
                      .map((amount) => parseInt(amount, 10))
                      .filter((num) => !isNaN(num))
                      .sort((a, b) => a - b)
                : [],
        [offerData?.offer]
    );

    const memberOfferAvailable = offerData?.memberOfferAvailable ?? true;
    const spouseOfferAvailable = offerData?.spouseOfferAvailable ?? true;

    const dobMonth = quoteFormData?.dateOfBirth?.month;
    const dobDay = quoteFormData?.dateOfBirth?.day;
    const dobYear = quoteFormData?.dateOfBirth?.year;
    const gender = quoteFormData?.gender;
    const stateValue = quoteFormData?.state;
    const nicotineUsage = quoteFormData?.nicotineUsage;
    const isNewYorkSelected = stateValue === "NY";

    const currentPremium = useMemo(
        () => coverageOptions?.find((opt) => opt.coverageAmount === selectedCoverage)?.monthlyPremium || 0,
        [coverageOptions, selectedCoverage]
    );

    const formValuesMatchSavedQuote =
        quoteData?.dobMonth === dobMonth &&
        quoteData?.dobDay === dobDay &&
        quoteData?.dobYear === dobYear &&
        quoteData?.gender === gender &&
        quoteData?.state === stateValue &&
        quoteData?.hasUsedNicotineLastYear === (nicotineUsage === "true");

    const displayName = offerData?.firstName || "Your";

    // ========== Effects ==========
    // Validate offer data exists
    useEffect(() => {
        if (!isDTO) return;
        if (!offerData) {
            console.error("Quote state validation error: Missing offer data");
            pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_EMPTY_OFFER);
            navigate(ROUTES.SOMETHING_WENT_WRONG);
        }
    }, [isDTO, offerData, navigate]);

    // Initialize coverage options from offer data
    useEffect(() => {
        if (offerCoverageAmounts.length > 0 && !isInitialized.current) {
            const initialOptions = offerCoverageAmounts.map((amount) => ({ coverageAmount: amount, monthlyPremium: 0 }));
            setCoverageOptionsState(initialOptions);
            setSelectedCoverage(offerCoverageAmounts[0]);
            isInitialized.current = true;
        }
    }, [offerCoverageAmounts, isInitialized]);

    // Restore form data from Redux
    useEffect(() => {
        if (!isDTO || !quoteData) {
            return;
        }

        restoreDateOfBirth(quoteData, setValue);
        restoreBasicFields(quoteData, setValue);

        if (quoteData.applicantType) {
            setActiveTab(quoteData.applicantType === "member" ? "myself" : "spouse");
        }

        const restoredOptions = mapCoverageOptionsWithPremiums(offerCoverageAmounts, quoteData.coverageOptions);
        setCoverageOptionsState(restoredOptions);

        if (quoteData.selectedCoverageAmount) {
            setSelectedCoverage(quoteData.selectedCoverageAmount);
        }
    }, [isDTO, quoteData, setValue, offerCoverageAmounts]);

    // Set default active tab based on offer availability
    useEffect(() => {
        if (!memberOfferAvailable && spouseOfferAvailable) {
            setActiveTab("spouse");
        } else if (memberOfferAvailable && !spouseOfferAvailable) {
            setActiveTab("myself");
        }
    }, [memberOfferAvailable, spouseOfferAvailable]);

    // ========== Handlers for Quote Fetching ==========
    const handleQuoteFetchSuccess = useCallback(
        (
            result: any,
            params: {
                stateValue: string;
                dobMonth: string;
                dobDay: string;
                dobYear: string;
                gender: string;
                nicotineUsage: string;
                activeTab: "myself" | "spouse";
            }
        ) => {
            if (!result.success || !result.coverageOptions) {
                return;
            }

            const updatedOptions = mapCoverageOptionsWithPremiums(offerCoverageAmounts, result.coverageOptions).filter(
                (option) => option.monthlyPremium > 0
            );

            setCoverageOptionsState(updatedOptions);

            // Determine the default coverage based on number of options
            const defaultIndex = getDefaultCoverageIndex(updatedOptions.length);
            const defaultCoverageAmount =
                updatedOptions[defaultIndex]?.coverageAmount ?? updatedOptions[0]?.coverageAmount ?? 0;
            const defaultCoveragePremium =
                updatedOptions[defaultIndex]?.monthlyPremium ?? updatedOptions[0]?.monthlyPremium ?? 0;

            let coverageToSave = defaultCoverageAmount;
            let premiumToSave = defaultCoveragePremium;
            if (selectedCoverage > 0) {
                // Check if the saved coverage is still valid in the updated options
                const savedOption = updatedOptions.find((opt) => opt.coverageAmount === selectedCoverage);
                if (savedOption) {
                    coverageToSave = savedOption.coverageAmount;
                    premiumToSave = savedOption.monthlyPremium;
                } else {
                    // Saved coverage is no longer valid, use default and update state
                    setSelectedCoverage(defaultCoverageAmount);
                }
            } else {
                // No saved coverage, set the default
                setSelectedCoverage(defaultCoverageAmount);
            }

            saveQuote({
                state: params.stateValue,
                dobMonth: params.dobMonth,
                dobDay: params.dobDay,
                dobYear: params.dobYear,
                gender: params.gender as "male" | "female",
                hasUsedNicotineLastYear: params.nicotineUsage === "true",
                applicantType: params.activeTab === "myself" ? "member" : "spouse",
                coverageOptions: updatedOptions,
                selectedCoverageAmount: coverageToSave,
                selectedCoveragePremium: premiumToSave,
            });

            pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_SAVE);
        },
        [offerCoverageAmounts, selectedCoverage, saveQuote]
    );

    const fetchQuotesIfValid = useCallback(async () => {
        if (!stateValue || !gender || !nicotineUsage || !dobMonth || !dobDay || !dobYear) {
            return;
        }

        const result = await fetchCoverageOptions({
            state: stateValue,
            gender: gender as "male" | "female",
            hasUsedNicotineLastYear: nicotineUsage === "true",
            dobMonth,
            dobDay,
            dobYear,
            offerCoverageAmounts: offerCoverageAmounts.length > 0 ? offerCoverageAmounts : undefined,
        });

        handleQuoteFetchSuccess(result, { stateValue, dobMonth, dobDay, dobYear, gender, nicotineUsage, activeTab });
    }, [
        stateValue,
        gender,
        nicotineUsage,
        dobMonth,
        dobDay,
        dobYear,
        offerCoverageAmounts,
        fetchCoverageOptions,
        handleQuoteFetchSuccess,
        activeTab,
    ]);

    const processQuoteForm = useCallback(async () => {
        if (!isDTO) return;
        if (isNewYorkSelected) {
            pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_STATE_NOT_AVAILABLE);
            return;
        }

        const allFieldsFilled = areAllFieldsFilled(dobMonth, dobDay, dobYear, gender, stateValue, nicotineUsage);

        if (!allFieldsFilled) {
            setIsQuoteFormValid(false);
            return;
        }

        const isFormValid = await validateQuoteFields(trigger);
        setIsQuoteFormValid(isFormValid);

        if (!isFormValid) {
            return;
        }

        const hasExistingQuote =
            quoteData?.coverageOptions &&
            quoteData?.coverageOptions.filter(
                (opt) => opt.monthlyPremium > 0 && opt.coverageAmount === quoteData?.selectedCoverageAmount
            ).length > 0;
        if (hasExistingQuote && formValuesMatchSavedQuote) {
            return;
        }

        await fetchQuotesIfValid();
    }, [
        isDTO,
        isNewYorkSelected,
        dobMonth,
        dobDay,
        dobYear,
        gender,
        stateValue,
        nicotineUsage,
        trigger,
        quoteData?.coverageOptions,
        quoteData?.selectedCoverageAmount,
        formValuesMatchSavedQuote,
        fetchQuotesIfValid,
    ]);

    // Fetch quotes when form is complete
    useEffect(() => {
        processQuoteForm();
    }, [processQuoteForm]);

    // ========== Event Handlers ==========
    const handleTabChange = useCallback(
        async (tabId: string) => {
            const newTab = tabId as "myself" | "spouse";

            if (newTab === activeTab) {
                return;
            }

            setActiveTab(newTab);

            const applicantType = newTab === "myself" ? "member" : "spouse";
            form.reset({
                quote: {
                    dateOfBirth: { month: "", day: "", year: "" },
                    gender: "" as any,
                    state: "",
                    nicotineUsage: "",
                    coveragePremium: 0,
                    applicantType: applicantType,
                },
            });

            clearQuote();
            resetApplicationKeepOffer({ preserveSessionId: true });

            const resetOptions = offerCoverageAmounts.map((amount) => ({ coverageAmount: amount, monthlyPremium: 0 }));
            setCoverageOptionsState(resetOptions);
            setSelectedCoverage(offerCoverageAmounts.length > 0 ? offerCoverageAmounts[0] : 0);

            if (offerData) {
                await loadSavedQuoteData(offerData.invitationCode, applicantType, offerCoverageAmounts);
            }

            pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_CLEAR);
        },
        [activeTab, form, clearQuote, resetApplicationKeepOffer, offerData, loadSavedQuoteData, offerCoverageAmounts]
    );

    const handleCoverageChange = useCallback(
        (value: number) => {
            setSelectedCoverage(value);
            const premium = coverageOptions?.find((opt) => opt.coverageAmount === value)?.monthlyPremium || 0;
            saveCoverageSelection(value, premium);
        },
        [coverageOptions, saveCoverageSelection]
    );

    const handleApplyNow = useCallback(() => {
        storeQuote();
        pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_APPLY_NOW);
        navigate(ROUTES.APPLICATION.CONTACT);
    }, [storeQuote, navigate]);

    return {
        // State
        activeTab,
        coverageOptions,
        selectedCoverage,
        isQuoteFormValid,
        isLoadingQuotes,

        // Computed
        offerCoverageAmounts,
        memberOfferAvailable,
        spouseOfferAvailable,
        currentPremium,
        displayName,
        isNewYorkSelected,

        // Form values
        dobMonth,
        dobDay,
        dobYear,
        gender,
        stateValue,
        nicotineUsage,

        // Handlers
        handleTabChange,
        handleCoverageChange,
        handleApplyNow,
    };
};
